<template>
  <!-- The welcome screen's greeting block, class for class and string for string. When the owner has
       a new brief this thread IS the day's welcome — FusionBody suppresses the ReyWelcome splash and
       lands them here instead — so the two must not read as two different screens.
       No rule under it, for the same reason: the welcome screen has none either, and a divider here
       made the greeting read as thread chrome rather than as the top of the screen. -->
  <div class="self-stretch flex flex-col items-center pt-8"
       data-test="brief-thread-header">
    <div class="self-stretch px-4 inline-flex justify-center items-center gap-2.5 pb-8">
      <div class="w-80 text-center text-text-link text-4xl font-extrabold leading-10" data-test="greeting">
        {{ greeting }}
      </div>
    </div>
    <div class="self-stretch inline-flex justify-center items-center gap-2.5 pb-6">
      <div class="w-56 self-stretch text-center text-text-link text-base leading-5" data-test="prompt">
        {{ $t('messaging.briefHeader.prompt') }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';

/**
 * The morning brief thread's own header: who it is for, and an invitation to read what Rey found.
 *
 * Its own component rather than markup in MessengerWindow for the reason the Scheduled tasks header is:
 * it renders for exactly one kind of thread, and a block in that template guarded on yet another thread
 * flag would be one more thing that file has to explain.
 *
 * <b>It deliberately does not offer to put a finding on a cadence.</b> The design has the header say
 * that any of these can be made recurring, and that is the right call — it is the feature's main
 * discovery path. But the promotion action is not built, so stating it here would be an interface
 * promising something nothing can do. The sentence belongs with the action, in the same change.
 */
export default {
  name: 'BriefThreadHeader',
  props: {
    /**
     * The owner-local day this brief covers, as yyyy-MM-dd. Passed through rather than derived from
     * "today": a brief read on Tuesday is still Monday's brief, and a header that greets the reader
     * about today would be describing a different thread than the one they are looking at.
     *
     * The header no longer prints the day — the greeting is the welcome screen's, which has no date in
     * it, and the composer's topic label is what names the day now. Kept because it is the thread's
     * identity, and because a header that wants to say "for Monday" again should not have to re-plumb it.
     */
    briefDate: {
      type: String,
      required: false,
      default: '',
    },
  },
  computed: {
    ...mapStores(useMainStore),
    user_access_info() {
      return this.mainStore.user_access_info;
    },
    /**
     * Named after the display name the welcome screen already uses, so the two greetings cannot address
     * the same person differently.
     */
    display_name() {
      return this.user_access_info?.displayName || this.$t('common.unknownUser');
    },
    /**
     * The welcome screen's own string, not a copy of it. Anything else would let the two greetings drift
     * apart, and this thread stands in for that screen on the mornings it opens instead of it.
     */
    greeting() {
      return this.$t('rey.welcome.greeting', { name: this.display_name });
    },
  },
}
</script>
